'use client'

import React from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Badge } from "./ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Textarea } from "./ui/textarea"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "./ui/form"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { expenseApi, chartOfAccountsApi, purchaseApi } from "../lib/api/accounting"
import { getCompanyId } from "../lib/config"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { toast } from "sonner"
import { 
  DollarSign, 
  Building, 
  FileText, 
  Calculator, 
  Plus, 
  CheckCircle
} from "lucide-react"
import { MoonLoader } from "./ui/moon-loader"
import { apiService } from "../lib/api"
import { ExpenseFormFields, ExpenseSummary } from "./expense-form"

const expenseSchema = z.object({
  companyId: z.string().min(1, 'Company is required'),
  expenseDate: z.string().min(1, 'Expense date is required'),
  referenceNumber: z.string().optional(), // Invoice/Receipt #
  currency: z.string().optional(),
  totalAmount: z.coerce.number().min(0).optional(), // Auto-calculated from line items
  taxAmount: z.coerce.number().min(0).optional(), // Auto-calculated from line items
  // Line items - now required and contains all details
  lineItems: z.array(z.object({
    itemCode: z.string().optional(),
    categoryId: z.string().optional(), // Category at line item level
    budgetId: z.string().optional(), // Budget at line item level
    description: z.string().min(1, 'Item description is required'),
    vendorId: z.string().optional(), // Vendor at line item level
    vendorName: z.string().optional(),
    quantity: z.coerce.number().positive('Quantity must be positive'),
    taxRate: z.coerce.number().min(0).max(100).optional(),
    taxType: z.string().optional(),
    unitPrice: z.coerce.number().positive('Unit price must be greater than 0')
  })).min(1, 'At least one line item is required')
})

type EditExpenseModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  expense: any | null
}

export function EditExpenseModal({ open, onOpenChange, expense }: EditExpenseModalProps) {
  const queryClient = useQueryClient()
  const companyId = getCompanyId()

  // Fetch company data
  const { data: companyData } = useQuery({
    queryKey: ['company', companyId],
    queryFn: async () => await apiService.getCompany(companyId),
    enabled: !!companyId
  })
  const companyName = companyData?.name || 'Company'

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ['expense-categories', companyId],
    queryFn: async () => {
      const result = await expenseApi.getExpenseCategories({ companyId }) as any
      if (Array.isArray(result)) return result
      if (result?.data && Array.isArray(result.data)) return result.data
      if (result?.items && Array.isArray(result.items)) return result.items
      return []
    },
    enabled: !!companyId
  })
  const filteredCategories = React.useMemo(() => {
    if (!categoriesData) return []
    if (Array.isArray(categoriesData)) return categoriesData
    if (categoriesData.items) return categoriesData.items
    if ((categoriesData as any).data) return (categoriesData as any).data
    return []
  }, [categoriesData])

  // Fetch budgets
  const { data: budgetsData } = useQuery({
    queryKey: ['budgets', companyId],
    queryFn: async () => {
      const result = await expenseApi.getBudgets() as any
      if (Array.isArray(result)) return result
      if (result?.data && Array.isArray(result.data)) return result.data
      if (result?.items && Array.isArray(result.items)) return result.items
      return []
    },
    enabled: !!companyId
  })
  const filteredBudgets = React.useMemo(() => {
    if (!budgetsData) return []
    if (Array.isArray(budgetsData)) return budgetsData
    if (budgetsData.items) return budgetsData.items
    if ((budgetsData as any).data) return (budgetsData as any).data
    return []
  }, [budgetsData])

  // Fetch vendors
  const { data: vendors } = useQuery({
    queryKey: ['vendors', companyId],
    queryFn: async () => {
      const result = await purchaseApi.getVendors(companyId) as any
      if (Array.isArray(result)) return result
      if (result?.data && Array.isArray(result.data)) return result.data
      if (result?.items && Array.isArray(result.items)) return result.items
      return []
    },
    enabled: !!companyId
  })

  const form = useForm({
    resolver: zodResolver(expenseSchema),
    defaultValues: {
      companyId: '',
      expenseDate: new Date().toISOString().split('T')[0],
      referenceNumber: '',
      currency: 'Frw',
      totalAmount: 0,
      taxAmount: 0,
      lineItems: []
    }
  })

  // Fetch expense details with line items when editing
  const { data: expenseDetails } = useQuery({
    queryKey: ['expense', expense?.id],
    queryFn: async () => {
      if (!expense?.id) return null
      return await expenseApi.getExpenseById(expense.id) as any
    },
    enabled: !!expense?.id && open
  })

  // Initialize form when expense details are loaded
  React.useEffect(() => {
    if (expenseDetails) {
      const expenseDate = expenseDetails.expenseDate 
        ? new Date(expenseDetails.expenseDate).toISOString().split('T')[0] 
        : new Date().toISOString().split('T')[0]
      
      // Load line items if they exist, otherwise create a single line item from expense data
      const lineItems = expenseDetails.lineItems && Array.isArray(expenseDetails.lineItems) && expenseDetails.lineItems.length > 0
        ? expenseDetails.lineItems.map((item: any) => ({
            itemCode: item.itemCode || undefined,
            categoryId: item.categoryId || undefined,
            budgetId: item.budgetId || undefined, // Budget at line item level
            description: item.description || '',
            vendorId: item.vendorId || undefined,
            vendorName: item.vendorName || undefined,
            quantity: Number(item.quantity || 1),
            taxRate: Number(item.taxRate || 0),
            taxType: item.taxType || (item.taxRate > 0 ? 'B' : 'A'),
            unitPrice: Number(item.unitPrice || 0)
          }))
        : [{
            itemCode: undefined,
            categoryId: expenseDetails.categoryId || undefined,
            description: expenseDetails.description || '',
            vendorId: expenseDetails.vendorId || undefined,
            vendorName: expenseDetails.vendorName || undefined,
            quantity: 1,
            taxRate: Number(expenseDetails.taxRate || 0),
            taxType: expenseDetails.taxRate > 0 ? 'B' : 'A',
            unitPrice: Number(expenseDetails.amount || expenseDetails.totalAmount || 0)
          }]

      // Calculate totals from line items
      const baseAmount = lineItems.reduce((sum: number, item: any) => {
        const qty = Number(item.quantity || 1)
        const price = Number(item.unitPrice || 0)
        return sum + (qty * price)
      }, 0)
      
      const taxAmount = lineItems.reduce((sum: number, item: any) => {
        const qty = Number(item.quantity || 1)
        const price = Number(item.unitPrice || 0)
        const taxRate = Number(item.taxRate || 0)
        return sum + (qty * price * (taxRate / 100))
      }, 0)
      
      const totalAmount = baseAmount + taxAmount

      form.reset({
        companyId: expenseDetails.companyId || companyId,
        expenseDate: expenseDate,
        referenceNumber: expenseDetails.referenceNumber || '',
        currency: expenseDetails.currency || 'Frw',
        totalAmount: totalAmount,
        taxAmount: taxAmount,
        lineItems: lineItems
      })
    }
  }, [expenseDetails, form, companyId])

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!expense) return
      
      // Line items are now required - calculate totals from them
      if (!data.lineItems || !Array.isArray(data.lineItems) || data.lineItems.length === 0) {
        throw new Error('Please add at least one line item')
      }
      
      // Calculate totals from line items
      const baseAmount = data.lineItems.reduce((sum: number, item: any) => {
        const qty = Number(item.quantity || 1)
        const price = Number(item.unitPrice || 0)
        return sum + (qty * price)
      }, 0)
      
      const taxAmount = data.lineItems.reduce((sum: number, item: any) => {
        const qty = Number(item.quantity || 1)
        const price = Number(item.unitPrice || 0)
        const taxRate = Number(item.taxRate || 0)
        return sum + (qty * price * (taxRate / 100))
      }, 0)
      
      const totalAmount = baseAmount + taxAmount
      
      // Generate description from line items if not provided
      const description = data.lineItems.map((item: any) => item.description).filter(Boolean).join(', ') || 'Expense with line items'
      
      // Clean up data - expense header only has: date, reference, totals, company
      // Category, description, vendor are at line item level
      const cleanedData = {
        companyId: data.companyId,
        expenseDate: data.expenseDate,
        referenceNumber: data.referenceNumber || undefined,
        currency: data.currency || 'Frw',
        amount: baseAmount,
        taxAmount,
        totalAmount,
        description, // Generated from line items
        // Line items with all details (category, description, vendor, etc.)
        lineItems: data.lineItems.map((item: any) => ({
          itemCode: item.itemCode || undefined,
          categoryId: item.categoryId || undefined,
          budgetId: item.budgetId || undefined, // Budget at line item level
          description: item.description,
          vendorId: item.vendorId || undefined,
          vendorName: item.vendorName || undefined,
          quantity: Number(item.quantity || 1),
          taxRate: Number(item.taxRate || 0),
          taxType: item.taxType || (item.taxRate > 0 ? 'B' : 'A'),
          unitPrice: Number(item.unitPrice || 0)
        }))
      }
      
      return await expenseApi.updateExpense(expense.id, cleanedData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      queryClient.invalidateQueries({ queryKey: ['budget-analytics'] })
      toast.success('Expense updated successfully')
      onOpenChange(false)
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to update expense'
      toast.error(errorMessage)
    }
  })

  const onSubmit = (data: any) => {
    updateMutation.mutate(data)
  }

  return (
    <Dialog open={open} onOpenChange={(open) => {
      if (!updateMutation.isPending) {
        onOpenChange(open)
        if (!open) {
          form.reset()
        }
      }
    }}>
      <DialogContent className="!max-w-7xl max-h-[95vh] overflow-hidden w-[95vw] flex flex-col bg-white">
        <DialogHeader className="pb-3 border-b border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center shadow-sm">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-teal-500 rounded-full border-2 border-white flex items-center justify-center">
                  <FileText className="w-2.5 h-2.5 text-white" />
                </div>
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-slate-900">
                  Edit Expense
                </DialogTitle>
                <DialogDescription className="text-slate-600 mt-0.5 text-xs">
                  Update expense details • Full audit trail • Real-time calculations
                </DialogDescription>
              </div>
            </div>
            <Badge className="bg-teal-100 text-teal-700 border-teal-200 px-3 py-1 text-xs font-semibold">
              {expense?.status || 'Draft'} Mode
            </Badge>
          </div>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col" style={{ maxHeight: 'calc(95vh - 140px)' }}>
            <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 hover:scrollbar-thumb-slate-400">
              <ExpenseFormFields
                form={form}
                companyName={companyName}
                filteredCategories={filteredCategories}
                filteredBudgets={filteredBudgets}
                vendors={vendors || []}
              />
              
              <div className="border-t border-slate-200 bg-slate-50 pt-3 px-4 pb-3 mt-4">
                <ExpenseSummary form={form} />
              </div>
            </div>
            
            <div className="flex-shrink-0 bg-white border-t border-slate-200 p-4 shadow-sm">
              <div className="flex items-center justify-between w-full">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => onOpenChange(false)}
                  disabled={updateMutation.isPending}
                  className="h-10 px-6 text-sm font-semibold border border-slate-300 hover:bg-slate-100 transition-all duration-200"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={updateMutation.isPending}
                  className="h-10 px-8 text-sm font-semibold bg-teal-600 hover:bg-teal-700 text-white shadow-sm hover:shadow-md transition-all duration-200"
                >
                  {updateMutation.isPending ? (
                    <>
                      <MoonLoader size="sm" className="mr-2" color="teal" />
                      Updating Expense...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Update Expense
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
